// @ts-check
/* eslint-env browser */

import { reactiveStore } from '../reactive-store.js'
import { getAllowedEntityPropertiesForEntity, getEntityType } from '../../../utils/entity-properties.js'

/**
 * A singleton class handling the auto-saving of features to the server by subscribing to reactiveStore.features
 *
 * Message protocol:
 * - no-save*: do not hit the API (caller already persisted, or must not persist)
 * - sync: ID rewrite after create; update internal snapshot only
 */
export class FeatureAutosave {
  #jobId
  #previousById = new Map()
  #queue = Promise.resolve()

  /**
   * @param {string} jobId
   */
  constructor (jobId) {
    this.#jobId = jobId
    this.#previousById = this.#toFeatureMap(reactiveStore.features.get())
    reactiveStore.features.subscribe(this.#subscriber.bind(this))
  }

  /**
   * @param {any[]} features
   * @returns {Map<string, any>}
   */
  #toFeatureMap (features) {
    const map = new Map()
    for (const feature of features) {
      if (!feature?.properties?._id || feature?.properties?._isMapOverlay) continue
      map.set(feature.properties._id, feature)
    }
    return map
  }

  /**
   * @param {any} feature
   * @returns {Record<string, string>}
   */
  #getEditablePropertiesSnapshot (feature) {
    if (!feature || !feature.properties) return {}
    const jobType = window['jobType'] /* eslint-disable-line dot-notation */
    const entityType = getEntityType(jobType, feature, false)
    const editable = getAllowedEntityPropertiesForEntity(feature, entityType) || {}
    return Object.fromEntries(
      Object.entries(editable).map(([key, value]) => [key, value == null ? '' : String(value)])
    )
  }

  /**
   * @param {any[]} currentFeatures
   * @param {string | undefined} message
   */
  #subscriber (currentFeatures, message) {
    // Filter out map overlays and other non-feature objects
    const filtered = (currentFeatures || []).filter(f => !f?.properties?._isMapOverlay)
    const currentById = this.#toFeatureMap(filtered)

    if (typeof message === 'string' && (message.startsWith('no-save') || message === 'sync')) {
      this.#previousById = currentById
      return
    }

    if (!this.#jobId) {
      this.#previousById = currentById
      return
    }

    const previousById = this.#previousById
    const previousIdSet = new Set(previousById.keys())
    const currentIdSet = new Set(currentById.keys())

    // New = non-empty _id not seen before (client UUID from add-feature, or undo restore).
    // Skip blank/missing ids so corrupt features cannot spuriously POST on every update.
    const addedFeatures = filtered.filter(f => {
      const id = f.properties?._id
      return typeof id === 'string' && id.trim() !== '' && !previousIdSet.has(id)
    })
    const removedIds = [...previousIdSet].filter(id => !currentIdSet.has(id))

    /** @type {Array<{id: string, feature: any, saveProps: boolean, saveGeom: boolean}>} */
    const changes = []
    for (const [id, currentFeature] of currentById.entries()) {
      if (!previousById.has(id)) continue
      const previousFeature = previousById.get(id)
      const saveProps = JSON.stringify(this.#getEditablePropertiesSnapshot(currentFeature)) !== JSON.stringify(this.#getEditablePropertiesSnapshot(previousFeature))
      const saveGeom = JSON.stringify(currentFeature?.geometry ?? null) !== JSON.stringify(previousFeature?.geometry ?? null)
      if (saveProps || saveGeom) {
        changes.push({ id, feature: currentFeature, saveProps, saveGeom })
      }
    }

    this.#previousById = currentById
    if (addedFeatures.length === 0 && removedIds.length === 0 && changes.length === 0) return

    const jobId = this.#jobId
    this.#queue = this.#queue.then(async () => {
      if (addedFeatures.length > 0) {
        const result = await this.#createAddedFeatures(
          addedFeatures,
          reactiveStore.features.get(),
          reactiveStore.selectedFeatureId.get()
        )
        if (result) {
          reactiveStore.features.set(result.features, 'sync')
          if (result.selectedFeatureId !== undefined) {
            reactiveStore.selectedFeatureId.set(result.selectedFeatureId)
          }
        }
      }

      for (const entityId of removedIds) {
        try {
          await FeatureAutosave.#deleteFeatureOnServer(entityId)
        } catch (error) {
          console.error('feature-autosave: Failed to delete feature', { id: entityId, error })
        }
      }

      if (changes.length === 0) return

      const geomChanges = changes.filter(c => c.saveGeom)
      const propChanges = changes.filter(c => c.saveProps)
      await Promise.all([
        ...(geomChanges.length > 0
          ? [FeatureAutosave.#bulkSaveGeometry(jobId, geomChanges).catch(error => console.error('feature-autosave: Failed to save geometry', { ids: geomChanges.map(c => c.id), error }))]
          : []),
        ...propChanges.map(change =>
          FeatureAutosave.#saveProperties(jobId, change.id, this.#getEditablePropertiesSnapshot(change.feature))
            .catch(error => console.error('feature-autosave: Failed to save properties', error))
        )
      ])
    })
  }

  /**
   * Create server entities for newly added features and return store updates (no side effects).
   * @param {any[]} addedFeatures
   * @param {any[]} currentFeatures
   * @param {string} selectedFeatureId
   * @returns {Promise<{ features: any[], selectedFeatureId?: string } | null>}
   */
  async #createAddedFeatures (addedFeatures, currentFeatures, selectedFeatureId) {
    const createdFeatures = []
    /** @type {Map<string, string>} */
    const clientIdToServerId = new Map()

    for (const originalFeature of addedFeatures) {
      try {
        const { _id, ...propertiesWithoutId } = originalFeature.properties ?? {}
        const createdFeature = await FeatureAutosave.#createFeatureOnServer(this.#jobId, {
          featureType: originalFeature.properties?.feature_type,
          properties: propertiesWithoutId,
          geometry: originalFeature.geometry
        })
        if (typeof _id === 'string' && _id && createdFeature?.properties?._id) {
          clientIdToServerId.set(_id, createdFeature.properties._id)
        }
        createdFeatures.push({
          ...createdFeature,
          geometry: (originalFeature?.geometry?.coordinates?.length > 0) ? originalFeature.geometry : createdFeature.geometry
        })
      } catch (error) {
        console.error('feature-autosave: Failed to create feature', error)
      }
    }

    if (createdFeatures.length === 0) return null

    const createdFeatureIds = new Set(
      createdFeatures.map(f => f.properties?._id).filter(id => id !== undefined && id !== null)
    )
    const addedFeatureIds = new Set(
      addedFeatures.map(f => f.properties?._id).filter(id => id !== undefined && id !== null)
    )
    // Drop client IDs being replaced and any server IDs we're about to re-insert
    const idsToFilter = new Set([...createdFeatureIds, ...addedFeatureIds])
    const featuresWithId = currentFeatures.filter(
      f => f.properties?._id && !idsToFilter.has(f.properties._id)
    )

    /** @type {{ features: any[], selectedFeatureId?: string }} */
    const result = { features: [...featuresWithId, ...createdFeatures] }
    if (selectedFeatureId && clientIdToServerId.has(selectedFeatureId)) {
      result.selectedFeatureId = clientIdToServerId.get(selectedFeatureId)
    }
    return result
  }

  /**
   * @param {string} jobId
   * @param {string} entityId
   * @param {Record<string, string>} properties
   */
  static async #saveProperties (jobId, entityId, properties) {
    const body = new URLSearchParams()
    for (const [key, value] of Object.entries(properties)) {
      body.append(key, value)
    }

    const response = await fetch(`/extractions/${jobId}/entity/${entityId}/properties`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/x-www-form-urlencoded'
      },
      body
    })

    if (!response.ok) {
      throw new Error('Failed to autosave feature properties')
    }
  }

  /**
   * @param {string} jobId
   * @param {Array<{id: string, feature: any}>} changes
   */
  static async #bulkSaveGeometry (jobId, changes) {
    const updates = changes.map(c => ({ id: c.id, geometry: c.feature.geometry }))
    const response = await fetch(`/api/extractions/${jobId}/bulk_geometry`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ updates })
    })

    if (!response.ok) {
      throw new Error('Failed to autosave feature geometry')
    }
  }

  /**
   * @param {string} jobId
   * @param {{ featureType: string, properties?: any, geometry?: any }} options
   * @returns {Promise<any>}
   */
  static async #createFeatureOnServer (jobId, { featureType, properties = null, geometry = null }) {
    const urlParams = new URLSearchParams({
      type: featureType === 'tree' ? 'tree' : 'treepreservationzone'
    })

    if (properties) {
      urlParams.append('properties', JSON.stringify(properties))
    }

    if (geometry) {
      urlParams.append('geometry', JSON.stringify(geometry))
    }

    const response = await fetch(`/extractions/${jobId}/entity/new`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/x-www-form-urlencoded'
      },
      body: urlParams
    })

    if (!response.ok) {
      throw new Error(`Failed to create feature: ${response.status} ${response.statusText}`)
    }

    const data = await response.json()
    return data.feature
  }

  /**
   * @param {string} entityId
   */
  static async #deleteFeatureOnServer (entityId) {
    const response = await fetch(`/api/extracted_object/${entityId}`, {
      method: 'DELETE'
    })

    if (!response.ok) {
      throw new Error(`Failed to delete feature: ${response.status} ${response.statusText}`)
    }
  }
}
